import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectCorkboardById,
  addNoteToCorkboard,
  updateNoteInCorkboard,
  removeNoteFromCorkboard,
  addDrawing,
  removeDrawing,
  updateDrawing,
  clearDrawings,
  updateViewState,
  resetViewState,
  updateCorkboard,
} from './corkboardsSlice';
import corkboardService from '../../utils/apiRequests/corkboardService';

const CorkboardContext = createContext(null);

/**
 * Generate a unique ID without uuid dependency
 */
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Tool modes available in the corkboard
 */
export const TOOL_MODES = {
  CURSOR: 'cursor',
  PAN: 'pan',
  PENCIL: 'pencil',
  LINE: 'line',
};

/**
 * CorkboardProvider manages all state for a single corkboard canvas
 */
export const CorkboardProvider = ({ corkboardId, children }) => {
  const dispatch = useDispatch();
  const corkboard = useSelector(selectCorkboardById(corkboardId));

  // Local state for interactions
  const [toolMode, setToolMode] = useState(TOOL_MODES.CURSOR);
  const [drawColor, setDrawColor] = useState('black'); // Color key, not hex value
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentDrawing, setCurrentDrawing] = useState(null);
  const [lineStart, setLineStart] = useState(null);
  const [lineStartAttachment, setLineStartAttachment] = useState(null);
  const [isPhantomNoteMode, setIsPhantomNoteMode] = useState(false);
  const [phantomNotePos, setPhantomNotePos] = useState(null);
  const [selectedDrawingId, setSelectedDrawingId] = useState(null);
  const [draggingEndpoint, setDraggingEndpoint] = useState(null); // { drawingId, pointIndex }
  const [draggingDrawing, setDraggingDrawing] = useState(null); // { drawingId, initialPoints, offset }

  // Undo/Redo history
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoAction = useRef(false);

  // Auto-save refs
  const viewStateSaveTimeoutRef = useRef(null);
  const notesDrawingsSaveTimeoutRef = useRef(null);
  const lastSavedViewStateRef = useRef(null);
  const lastSavedNotesDrawingsRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  // Initialize history with current state
  useEffect(() => {
    if (corkboard && history.length === 0) {
      setHistory([{ notes: corkboard.notes, drawings: corkboard.drawings }]);
      setHistoryIndex(0);
    }
  }, [corkboard, history.length]);

  // Save state to history (for undo/redo)
  const saveToHistory = useCallback(() => {
    if (isUndoRedoAction.current || !corkboard) {
      isUndoRedoAction.current = false;
      return;
    }

    const newState = { notes: [...corkboard.notes], drawings: [...corkboard.drawings] };
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newState);

    // Limit history to 50 entries
    if (newHistory.length > 50) {
      newHistory.shift();
    }

    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [corkboard, history, historyIndex]);

  // View state - memoized to prevent unnecessary re-renders
  const viewState = useMemo(
    () => corkboard?.viewState || { panX: 0, panY: 0, zoom: 1 },
    [corkboard?.viewState]
  );

  const setPan = useCallback(
    (panX, panY) => {
      dispatch(updateViewState({ corkboardId, viewState: { panX, panY } }));
    },
    [dispatch, corkboardId]
  );

  const setZoom = useCallback(
    (zoom) => {
      const clampedZoom = Math.max(0.1, Math.min(5, zoom));
      dispatch(updateViewState({ corkboardId, viewState: { zoom: clampedZoom } }));
    },
    [dispatch, corkboardId]
  );

  const resetZoomFn = useCallback(() => {
    dispatch(resetViewState({ corkboardId }));
  }, [dispatch, corkboardId]);

  // Note operations
  const addNote = useCallback(
    (noteData = {}) => {
      const newNote = {
        _id: generateId(),
        title: noteData.title || 'New Note',
        content: noteData.content || '',
        createdAt: Date.now(),
        updatedEpoch: Date.now(),
        deletedTimestamp: null,
        // Don't set default color/textColor - let CorkboardNote use theme-aware defaults
        color: noteData.color || null,
        textColor: noteData.textColor || null,
        relatedChapters: [],
        relatedCharacters: [],
        relatedNotes: [],
        type: noteData.type || 'generic',
        tags: noteData.tags || [],
        // Corkboard-specific fields
        x: noteData.x ?? 100,
        y: noteData.y ?? 100,
        width: noteData.width ?? 200,
        height: noteData.height ?? 150,
      };
      dispatch(addNoteToCorkboard({ corkboardId, note: newNote }));
      saveToHistory();
      return newNote;
    },
    [dispatch, corkboardId, saveToHistory]
  );

  const updateNote = useCallback(
    (noteId, updates) => {
      dispatch(updateNoteInCorkboard({ corkboardId, noteId, updates }));
      saveToHistory();
    },
    [dispatch, corkboardId, saveToHistory]
  );

  const deleteNote = useCallback(
    (noteId) => {
      // Find and delete all drawings attached to this note
      const drawingsList = corkboard?.drawings || [];
      const drawingsToDelete = drawingsList.filter((drawing) => {
        if (!drawing.attachments) return false;
        // Check if any attachment references this note
        return drawing.attachments.some((attachment) => attachment && attachment.noteId === noteId);
      });

      // Delete attached drawings first
      drawingsToDelete.forEach((drawing) => {
        dispatch(removeDrawing({ corkboardId, drawingId: drawing.id }));
      });

      // Then delete the note
      dispatch(removeNoteFromCorkboard({ corkboardId, noteId }));
      saveToHistory();
    },
    [dispatch, corkboardId, saveToHistory, corkboard]
  );

  // Calculate point position from attachment info
  const getPointFromAttachment = useCallback((attachment, note) => {
    if (!attachment || !note) return null;
    const { x, y, width, height } = note;
    const { position, type } = attachment;

    if (type === 'corner') {
      switch (position) {
        case 'top-left':
          return { x, y };
        case 'top-right':
          return { x: x + width, y };
        case 'bottom-left':
          return { x, y: y + height };
        case 'bottom-right':
          return { x: x + width, y: y + height };
        default:
          return null;
      }
    } else if (type === 'edge-center') {
      switch (position) {
        case 'top':
          return { x: x + width / 2, y };
        case 'bottom':
          return { x: x + width / 2, y: y + height };
        case 'left':
          return { x, y: y + height / 2 };
        case 'right':
          return { x: x + width, y: y + height / 2 };
        default:
          return null;
      }
    }
    return null;
  }, []);

  // Update all drawings attached to a note when it moves or resizes
  // Optimized to batch all updates into a single operation
  const updateAttachedDrawings = useCallback(
    (noteId, updatedNote, skipHistory = false) => {
      const note =
        updatedNote || corkboard?.notes?.find((n) => (n._id || n.id || n.noteId) === noteId);
      if (!note) return;

      const drawingsList = corkboard?.drawings || [];
      const updates = [];

      drawingsList.forEach((drawing) => {
        if (drawing.type === 'line' && drawing.points && drawing.attachments) {
          let needsUpdate = false;
          const newPoints = drawing.points.map((point, index) => {
            const attachment = drawing.attachments?.[index];
            if (attachment && attachment.noteId === noteId) {
              const newPoint = getPointFromAttachment(attachment, note);
              if (newPoint) {
                needsUpdate = true;
                return newPoint;
              }
            }
            return point;
          });

          if (needsUpdate) {
            updates.push({
              drawingId: drawing.id,
              points: newPoints,
            });
          }
        }
      });

      // Batch all drawing updates
      if (updates.length > 0) {
        updates.forEach((update) => {
          dispatch(
            updateDrawing({
              corkboardId,
              drawingId: update.drawingId,
              updates: { points: update.points },
            })
          );
        });
        // Only save to history once for the batch, and only if not skipping
        if (!skipHistory && updates.length > 0) {
          saveToHistory();
        }
      }
    },
    [corkboard, dispatch, corkboardId, getPointFromAttachment, saveToHistory]
  );

  const moveNote = useCallback(
    (noteId, x, y, skipAttachedUpdates = false) => {
      const note = corkboard?.notes?.find((n) => (n._id || n.id || n.noteId) === noteId);
      const updatedNote = note ? { ...note, x, y } : null;
      dispatch(updateNoteInCorkboard({ corkboardId, noteId, updates: { x, y } }));
      // Update all drawings attached to this note (skip during drag for performance)
      if (updatedNote && !skipAttachedUpdates) {
        updateAttachedDrawings(noteId, updatedNote, true); // Skip history during drag
      }
    },
    [dispatch, corkboardId, corkboard, updateAttachedDrawings]
  );

  // Bring a note to the front (highest z-index)
  const bringNoteToFront = useCallback(
    (noteId) => {
      const notesList = corkboard?.notes || [];
      // Find the current highest zIndex
      const maxZIndex = notesList.reduce((max, n) => Math.max(max, n.zIndex || 0), 0);
      // Set this note's zIndex to be higher
      dispatch(updateNoteInCorkboard({ corkboardId, noteId, updates: { zIndex: maxZIndex + 1 } }));
    },
    [dispatch, corkboardId, corkboard]
  );

  // Separate function to update attached drawings after drag ends
  const finalizeNoteMove = useCallback(
    (noteId) => {
      const note = corkboard?.notes?.find((n) => (n._id || n.id || n.noteId) === noteId);
      if (note) {
        updateAttachedDrawings(noteId, note, false); // Save to history on finalize
        // Bring the moved note to front
        bringNoteToFront(noteId);
      }
    },
    [corkboard, updateAttachedDrawings, bringNoteToFront]
  );

  const resizeNote = useCallback(
    (noteId, width, height) => {
      const note = corkboard?.notes?.find((n) => (n._id || n.id || n.noteId) === noteId);
      const updatedNote = note ? { ...note, width, height } : null;
      dispatch(updateNoteInCorkboard({ corkboardId, noteId, updates: { width, height } }));
      // Update all drawings attached to this note
      if (updatedNote) {
        updateAttachedDrawings(noteId, updatedNote);
      }
    },
    [dispatch, corkboardId, corkboard, updateAttachedDrawings]
  );

  // Drawing operations
  const startDrawing = useCallback(
    (point, attachment = null) => {
      if (toolMode === TOOL_MODES.PENCIL) {
        setIsDrawing(true);
        setCurrentDrawing({
          id: generateId(),
          type: 'freehand',
          color: drawColor,
          points: [point],
        });
      } else if (toolMode === TOOL_MODES.LINE) {
        if (!lineStart) {
          setLineStart(point);
          setLineStartAttachment(attachment);
        } else {
          // Complete the line
          // Attachments array must match point indices: [0] = start point, [1] = end point
          const hasAnyAttachment = lineStartAttachment || attachment;
          const attachments = hasAnyAttachment
            ? [lineStartAttachment || null, attachment || null]
            : undefined;
          const newDrawing = {
            id: generateId(),
            type: 'line',
            color: drawColor,
            points: [lineStart, point],
            attachments,
          };
          dispatch(addDrawing({ corkboardId, drawing: newDrawing }));
          // Chain lines: use the end point as the start of the next line
          setLineStart(point);
          setLineStartAttachment(attachment);
          saveToHistory();
        }
      }
    },
    [toolMode, drawColor, lineStart, lineStartAttachment, dispatch, corkboardId, saveToHistory]
  );

  const continueDrawing = useCallback(
    (point) => {
      if (isDrawing && toolMode === TOOL_MODES.PENCIL && currentDrawing) {
        setCurrentDrawing((prev) => ({
          ...prev,
          points: [...prev.points, point],
        }));
      }
    },
    [isDrawing, toolMode, currentDrawing]
  );

  const endDrawing = useCallback(() => {
    if (isDrawing && currentDrawing && currentDrawing.points.length > 1) {
      dispatch(addDrawing({ corkboardId, drawing: currentDrawing }));
      saveToHistory();
    }
    setIsDrawing(false);
    setCurrentDrawing(null);
  }, [isDrawing, currentDrawing, dispatch, corkboardId, saveToHistory]);

  const cancelLineDrawing = useCallback(() => {
    setLineStart(null);
    setLineStartAttachment(null);
  }, []);

  const deleteDrawing = useCallback(
    (drawingId) => {
      dispatch(removeDrawing({ corkboardId, drawingId }));
      if (selectedDrawingId === drawingId) {
        setSelectedDrawingId(null);
      }
      saveToHistory();
    },
    [dispatch, corkboardId, saveToHistory, selectedDrawingId]
  );

  const updateDrawingPoints = useCallback(
    (drawingId, points, attachments = null) => {
      const updates = { points };
      if (attachments !== null) {
        updates.attachments = attachments;
      }
      dispatch(updateDrawing({ corkboardId, drawingId, updates }));
      saveToHistory();
    },
    [dispatch, corkboardId, saveToHistory]
  );

  const updateDrawingColor = useCallback(
    (drawingId, color) => {
      dispatch(updateDrawing({ corkboardId, drawingId, updates: { color } }));
      saveToHistory();
    },
    [dispatch, corkboardId, saveToHistory]
  );

  // Helper function to calculate distance from point to line segment
  const distanceToLineSegment = (point, lineStartPoint, lineEndPoint) => {
    const A = point.x - lineStartPoint.x;
    const B = point.y - lineStartPoint.y;
    const C = lineEndPoint.x - lineStartPoint.x;
    const D = lineEndPoint.y - lineStartPoint.y;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;

    if (lenSq !== 0) {
      param = dot / lenSq;
    }

    let xx;
    let yy;

    if (param < 0) {
      xx = lineStartPoint.x;
      yy = lineStartPoint.y;
    } else if (param > 1) {
      xx = lineEndPoint.x;
      yy = lineEndPoint.y;
    } else {
      xx = lineStartPoint.x + param * C;
      yy = lineStartPoint.y + param * D;
    }

    const dx = point.x - xx;
    const dy = point.y - yy;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Hit detection for drawings
  const hitTestDrawing = useCallback(
    (point, zoom) => {
      const hitThreshold = 20 / zoom; // Increased threshold for easier clicking
      const drawings = corkboard?.drawings || [];
      const notesList = corkboard?.notes || [];

      // Helper to get actual point position (handles attachments)
      const getActualPoint = (drawingPoint, attachment) => {
        if (attachment && attachment.noteId) {
          const note = notesList.find((n) => (n._id || n.id || n.noteId) === attachment.noteId);
          if (note) {
            return getPointFromAttachment(attachment, note) || drawingPoint;
          }
        }
        return drawingPoint;
      };

      // Check drawings in reverse order (most recent first)
      for (let i = drawings.length - 1; i >= 0; i -= 1) {
        const drawing = drawings[i];

        if (drawing.type === 'line' && drawing.points.length === 2) {
          // Get actual point positions (accounting for attachments)
          const p1 = getActualPoint(drawing.points[0], drawing.attachments?.[0]);
          const p2 = getActualPoint(drawing.points[1], drawing.attachments?.[1]);
          const dist = distanceToLineSegment(point, p1, p2);
          if (dist < hitThreshold) {
            return drawing.id;
          }
        } else if (drawing.type === 'freehand' && drawing.points.length > 1) {
          // Check if point is near any segment of the freehand path
          for (let j = 0; j < drawing.points.length - 1; j += 1) {
            const p1 = getActualPoint(drawing.points[j], drawing.attachments?.[j]);
            const p2 = getActualPoint(drawing.points[j + 1], drawing.attachments?.[j + 1]);
            const dist = distanceToLineSegment(point, p1, p2);
            if (dist < hitThreshold) {
              return drawing.id;
            }
          }
        }
      }
      return null;
    },
    [corkboard, getPointFromAttachment]
  );

  // Check if point is near an endpoint (for dragging)
  const hitTestEndpoint = useCallback(
    (point, zoom) => {
      const hitThreshold = 12 / zoom;
      const drawings = corkboard?.drawings || [];
      const notesList = corkboard?.notes || [];

      // Helper to get actual point position (handles attachments)
      const getActualPoint = (drawingPoint, attachment) => {
        if (attachment && attachment.noteId) {
          const note = notesList.find((n) => (n._id || n.id || n.noteId) === attachment.noteId);
          if (note) {
            return getPointFromAttachment(attachment, note) || drawingPoint;
          }
        }
        return drawingPoint;
      };

      for (let i = drawings.length - 1; i >= 0; i -= 1) {
        const drawing = drawings[i];
        if (drawing.type === 'line' && drawing.points.length === 2) {
          // Get actual point positions (accounting for attachments)
          const p1 = getActualPoint(drawing.points[0], drawing.attachments?.[0]);
          const p2 = getActualPoint(drawing.points[1], drawing.attachments?.[1]);
          const dist1 = Math.sqrt((point.x - p1.x) ** 2 + (point.y - p1.y) ** 2);
          const dist2 = Math.sqrt((point.x - p2.x) ** 2 + (point.y - p2.y) ** 2);

          if (dist1 < hitThreshold) {
            return { drawingId: drawing.id, pointIndex: 0 };
          }
          if (dist2 < hitThreshold) {
            return { drawingId: drawing.id, pointIndex: 1 };
          }
        }
      }
      return null;
    },
    [corkboard, getPointFromAttachment]
  );

  const clearAllDrawings = useCallback(() => {
    dispatch(clearDrawings({ corkboardId }));
    saveToHistory();
  }, [dispatch, corkboardId, saveToHistory]);

  // Snap detection - finds the nearest snap point within threshold
  const findSnapPoint = useCallback(
    (point, zoom, snapThreshold = 15) => {
      const threshold = snapThreshold / zoom;
      const snapPoints = [];
      let nearestPoint = null;
      let nearestDistance = threshold;

      // Get snap points from notes (corners and edge centers)
      const notesList = corkboard?.notes || [];
      notesList.forEach((note) => {
        const noteId = note._id || note.id || note.noteId;
        const { x, y, width, height } = note;
        // Corners
        snapPoints.push({ x, y, type: 'corner', position: 'top-left', noteId }); // Top-left
        snapPoints.push({ x: x + width, y, type: 'corner', position: 'top-right', noteId }); // Top-right
        snapPoints.push({ x, y: y + height, type: 'corner', position: 'bottom-left', noteId }); // Bottom-left
        snapPoints.push({
          x: x + width,
          y: y + height,
          type: 'corner',
          position: 'bottom-right',
          noteId,
        }); // Bottom-right
        // Edge centers
        snapPoints.push({ x: x + width / 2, y, type: 'edge-center', position: 'top', noteId }); // Top center
        snapPoints.push({
          x: x + width / 2,
          y: y + height,
          type: 'edge-center',
          position: 'bottom',
          noteId,
        }); // Bottom center
        snapPoints.push({ x, y: y + height / 2, type: 'edge-center', position: 'left', noteId }); // Left center
        snapPoints.push({
          x: x + width,
          y: y + height / 2,
          type: 'edge-center',
          position: 'right',
          noteId,
        }); // Right center
      });

      // Get snap points from line endpoints
      const drawingsList = corkboard?.drawings || [];
      drawingsList.forEach((drawing) => {
        if (drawing.type === 'line' && drawing.points.length === 2) {
          drawing.points.forEach((p) => {
            snapPoints.push({ x: p.x, y: p.y, type: 'line-endpoint' });
          });
        }
      });

      // Find nearest snap point
      snapPoints.forEach((snapPoint) => {
        const distance = Math.sqrt((point.x - snapPoint.x) ** 2 + (point.y - snapPoint.y) ** 2);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestPoint = snapPoint;
        }
      });

      return nearestPoint
        ? {
            x: nearestPoint.x,
            y: nearestPoint.y,
            type: nearestPoint.type,
            noteId: nearestPoint.noteId,
            position: nearestPoint.position,
          }
        : null;
    },
    [corkboard]
  );

  // Undo/Redo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedoAction.current = true;
      const prevState = history[historyIndex - 1];
      dispatch(
        updateCorkboard({
          id: corkboardId,
          notes: prevState.notes,
          drawings: prevState.drawings,
        })
      );
      setHistoryIndex(historyIndex - 1);
    }
  }, [historyIndex, history, dispatch, corkboardId]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedoAction.current = true;
      const nextState = history[historyIndex + 1];
      dispatch(
        updateCorkboard({
          id: corkboardId,
          notes: nextState.notes,
          drawings: nextState.drawings,
        })
      );
      setHistoryIndex(historyIndex + 1);
    }
  }, [historyIndex, history, dispatch, corkboardId]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  // Phantom note operations
  const startPhantomNoteMode = useCallback(() => {
    setIsPhantomNoteMode(true);
    // Initialize with a default position (will be updated on mouse move)
    setPhantomNotePos({ x: 100, y: 100 });
  }, []);

  const cancelPhantomNoteMode = useCallback(() => {
    setIsPhantomNoteMode(false);
    setPhantomNotePos(null);
  }, []);

  const placePhantomNote = useCallback(
    (x, y) => {
      if (isPhantomNoteMode) {
        addNote({ x, y });
        setIsPhantomNoteMode(false);
        setPhantomNotePos(null);
      }
    },
    [isPhantomNoteMode, addNote]
  );

  const updatePhantomNotePos = useCallback(
    (x, y) => {
      if (isPhantomNoteMode) {
        setPhantomNotePos({ x, y });
      }
    },
    [isPhantomNoteMode]
  );

  const value = useMemo(
    () => ({
      // Corkboard data
      corkboard,
      corkboardId,
      notes: corkboard?.notes || [],
      drawings: corkboard?.drawings || [],

      // View state
      viewState,
      setPan,
      setZoom,
      resetZoom: resetZoomFn,

      // Tool mode
      toolMode,
      setToolMode,
      drawColor,
      setDrawColor,

      // Note operations
      addNote,
      updateNote,
      deleteNote,
      moveNote,
      resizeNote,
      finalizeNoteMove,

      // Phantom note operations
      isPhantomNoteMode,
      phantomNotePos,
      startPhantomNoteMode,
      cancelPhantomNoteMode,
      placePhantomNote,
      updatePhantomNotePos,

      // Drawing state and operations
      isDrawing,
      currentDrawing,
      lineStart,
      startDrawing,
      continueDrawing,
      endDrawing,
      cancelLineDrawing,
      deleteDrawing,
      updateDrawingPoints,
      updateDrawingColor,
      clearAllDrawings,
      selectedDrawingId,
      setSelectedDrawingId,
      draggingEndpoint,
      setDraggingEndpoint,
      draggingDrawing,
      setDraggingDrawing,
      hitTestDrawing,
      hitTestEndpoint,
      findSnapPoint,

      // Undo/Redo
      undo,
      redo,
      canUndo,
      canRedo,
    }),
    [
      corkboard,
      corkboardId,
      viewState,
      setPan,
      setZoom,
      resetZoomFn,
      toolMode,
      drawColor,
      addNote,
      updateNote,
      deleteNote,
      moveNote,
      resizeNote,
      finalizeNoteMove,
      isPhantomNoteMode,
      phantomNotePos,
      startPhantomNoteMode,
      cancelPhantomNoteMode,
      placePhantomNote,
      updatePhantomNotePos,
      isDrawing,
      currentDrawing,
      lineStart,
      startDrawing,
      continueDrawing,
      endDrawing,
      cancelLineDrawing,
      deleteDrawing,
      updateDrawingPoints,
      updateDrawingColor,
      clearAllDrawings,
      selectedDrawingId,
      setSelectedDrawingId,
      draggingEndpoint,
      setDraggingEndpoint,
      draggingDrawing,
      setDraggingDrawing,
      hitTestDrawing,
      hitTestEndpoint,
      findSnapPoint,
      undo,
      redo,
      canUndo,
      canRedo,
    ]
  );

  // Auto-save functionality
  useEffect(() => {
    if (!corkboard || !corkboard.projectId || isInitialLoadRef.current) {
      if (corkboard) {
        // Mark initial load as complete
        isInitialLoadRef.current = false;
        lastSavedViewStateRef.current = JSON.stringify(corkboard.viewState);
        lastSavedNotesDrawingsRef.current = JSON.stringify({
          notes: corkboard.notes,
          drawings: corkboard.drawings,
        });
      }
      return () => {
        // No cleanup needed on early return
      };
    }

    // Auto-save viewState with debouncing (1 second delay for pan/zoom)
    const currentViewStateStr = JSON.stringify(corkboard.viewState);
    if (currentViewStateStr !== lastSavedViewStateRef.current) {
      // Clear existing timeout
      if (viewStateSaveTimeoutRef.current) {
        clearTimeout(viewStateSaveTimeoutRef.current);
      }

      // Set new timeout
      viewStateSaveTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await corkboardService.updateCorkboard(
            corkboard.projectId,
            corkboardId,
            { viewState: corkboard.viewState }
          );

          if (response.error) {
            console.error('Error auto-saving viewState:', response.error);
          } else {
            lastSavedViewStateRef.current = currentViewStateStr;
          }
        } catch (err) {
          console.error('Error auto-saving viewState:', err);
        }
      }, 1000); // 1 second debounce for viewState
    }

    // Auto-save notes/drawings with debouncing (500ms delay)
    const currentNotesDrawingsStr = JSON.stringify({
      notes: corkboard.notes,
      drawings: corkboard.drawings,
    });
    if (currentNotesDrawingsStr !== lastSavedNotesDrawingsRef.current) {
      // Clear existing timeout
      if (notesDrawingsSaveTimeoutRef.current) {
        clearTimeout(notesDrawingsSaveTimeoutRef.current);
      }

      // Set new timeout
      notesDrawingsSaveTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await corkboardService.updateCorkboard(
            corkboard.projectId,
            corkboardId,
            {
              notes: corkboard.notes,
              drawings: corkboard.drawings,
            }
          );

          if (response.error) {
            console.error('Error auto-saving notes/drawings:', response.error);
          } else {
            lastSavedNotesDrawingsRef.current = currentNotesDrawingsStr;
          }
        } catch (err) {
          console.error('Error auto-saving notes/drawings:', err);
        }
      }, 500); // 500ms debounce for notes/drawings
    }

    // Cleanup function
    return () => {
      if (viewStateSaveTimeoutRef.current) {
        clearTimeout(viewStateSaveTimeoutRef.current);
      }
      if (notesDrawingsSaveTimeoutRef.current) {
        clearTimeout(notesDrawingsSaveTimeoutRef.current);
      }
    };
  }, [corkboard, corkboardId]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (viewStateSaveTimeoutRef.current) {
        clearTimeout(viewStateSaveTimeoutRef.current);
      }
      if (notesDrawingsSaveTimeoutRef.current) {
        clearTimeout(notesDrawingsSaveTimeoutRef.current);
      }
    };
  }, []);

  return <CorkboardContext.Provider value={value}>{children}</CorkboardContext.Provider>;
};

CorkboardProvider.propTypes = {
  corkboardId: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access corkboard context
 */
export const useCorkboard = () => {
  const context = useContext(CorkboardContext);
  if (!context) {
    throw new Error('useCorkboard must be used within a CorkboardProvider');
  }
  return context;
};

export default CorkboardProvider;
